import React, {Fragment} from 'react';

import {createSelector} from '@reduxjs/toolkit';
import {Map} from 'immutable';
import {colors} from 'js-libs/sweetcommons/styles';
import {DefaultRootState, useSelector} from 'react-redux';
import {Link} from 'react-router';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap';
import styled from 'styled-components';

import {UserAvatar} from 'app/components/general/avatars';
import {CurrentUserSelector, useLogout} from 'app/current-user/api';
import {useAccountSettings} from 'app/settings/api';
import {ACCOUNT_SWITCH_URL} from 'app/urls';
import {gt} from 'app/utils/i18n';

import {AuthenticatedUserSerializedType, UserSerializedType} from 'sweet-types';

export function UserMenu(): JSX.Element {
  const [requestLogout] = useLogout();

  const accountName = useAccountSettings(undefined, {
    refetchOnMountOrArgChange: 900,
  }).data!.account_name;
  const {isStaff, numAccounts, user} = useSelector(settingsAndUserSelector);

  return (
    <UserMenuButton userData={user}>
      <DropdownMenu>
        {isStaff ? (
          <DropdownItem key="1" href="/a/sp-admin">
            <i className="far fa-fw fa-cogs" />
            <span>{gt('Admin')}</span>
          </DropdownItem>
        ) : null}
        <DropdownItem key="3" href="/accounts/overview/">
          <i className="fa fa-fw fa-user-circle" />
          <span>{gt('My Account')}</span>
        </DropdownItem>
        {numAccounts > 1 ? (
          <Fragment>
            <DropdownItem divider />
            <DropdownItem tag={Link} key="2" to={ACCOUNT_SWITCH_URL}>
              <div className="d-flex">
                <div>
                  <i className="fa fa-fw fa-external-link mt-1" />
                </div>
                <div>
                  {gt('Switch Account')}
                  <div className="curr-account small text-muted font-weight-bold text-truncate">
                    {accountName}
                  </div>
                </div>
              </div>
            </DropdownItem>
          </Fragment>
        ) : null}
        <DropdownItem divider />
        <DropdownItem key="4" onClick={() => requestLogout()}>
          <i className="fa fa-fw fa-sign-out" />
          <span>{gt('Logout')}</span>
        </DropdownItem>
      </DropdownMenu>
    </UserMenuButton>
  );
}

export const settingsAndUserSelector = createSelector<
  [
    (state: DefaultRootState) => AuthenticatedUserSerializedType,
    (state: DefaultRootState) => Map<string, Map<string, UserSerializedType>>
  ],
  {
    isStaff: boolean;
    numAccounts: number;
    user: UserSerializedType;
  }
>(
  [
    (state) => CurrentUserSelector(state).data!,
    (state) => state.userArea.getIn(['users', 'byId']),
  ],
  (user, usersMap) => ({
    isStaff: user.is_staff,
    numAccounts: user.num_accounts,
    user: usersMap.get(user.id.toString())!.toJS() as UserSerializedType,
  })
);

export interface UserMenuButtonProps {
  userData: UserSerializedType;
  children: React.ReactNode;
  className?: string;
}

export const UserMenuButton = ({
  userData,
  children,
  className = 'd-none d-lg-flex',
}: UserMenuButtonProps): JSX.Element => (
  <StyledUserMenuContainerDiv className={className}>
    <DropdownToggle
      className="menu-button nav-link d-flex align-items-center"
      tag="a"
    >
      <UserAvatar user={userData} />
      <span className="text-truncate name ml-2 d-none d-xl-block">
        {userData.name}
      </span>
      <i className="ml-3 fa fa-angle-down" />
    </DropdownToggle>
    {children}
  </StyledUserMenuContainerDiv>
);

const StyledUserMenuContainerDiv = styled(UncontrolledDropdown)`
  align-self: stretch;
  background: ${colors.darkGreen};
  align-items: stretch;

  .menu-button {
    padding: 0 1.5rem !important;
    color: ${colors.blueGray};

    &,
    > * {
      transition: 250ms;
    }

    > .name {
      max-width: 200px;
    }

    > span,
    > i {
      color: ${colors.blueGray};
    }

    &:hover {
      cursor: pointer;
      > i,
      > span {
        color: #fff;
      }

      .avatar {
        border-color: #fff;
      }
    }

    .avatar {
      border: 2px solid ${colors.blueGray};
    }
  }

  .dropdown-item i {
    margin-right: 0.5rem;
  }
  .curr-account {
    max-width: 230px;
  }
`;
